import { TEXTS } from '../../core/texts';
import React, { ChangeEvent, useCallback, useState } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { BoxFullWidth, ColumnBoxFlexFullWidth, FlexBoxFullWidth } from '../common/boxes';
import InputWithIcon from '../common/InputWithIcon';
import { ButtonWithLoader } from '../common/ButtonWithLoader';
import { Validator } from '../../core/Validator';
import { ValidationErrorState } from '../../types/validationErrorState';
import { useMutation } from '@tanstack/react-query';
import { AdminAuthService } from '../../services/admin-auth';
import { ILoginProps } from '../../interfaces/loginProps';
import { AxiosErrorResponse } from '../../interfaces/errorResponse';
import { useNotification } from '../../+state/useNotification';
import { parseAxiosError } from '../../services/axios';
import { useAuthState } from '../../+state/useAuthState';

const Login = () => {
  const theme = useTheme();
  const { openErrorToast } = useNotification();
  const { setUser: setLoggedInUser } = useAuthState();
  const [user, setUser] = useState({ email: '', password: '' });
  const [responseError, setResponseError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<ValidationErrorState>({});
  const handleInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setUser(prev => ({ ...prev, [e.target?.name]: e.target.value }));
  }, []);

  const mutation = useMutation({
    mutationFn: (data: ILoginProps) => AdminAuthService.login(data),
    onSuccess: (data) => setLoggedInUser(data),
    onError: (err: AxiosErrorResponse) => {
      const msg = parseAxiosError(err);
      setResponseError(msg);
      openErrorToast(msg);
    }
  });

  const submitFormHandler = useCallback((e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setResponseError(null);
    const validationErrors = Validator.validate(user, Validator.NAMES.AUTH);
    if (validationErrors) {
      return setValidationError(validationErrors);
    }
    setValidationError({});
    mutation.mutate({ email: user.email, password: user.password });
  }, [user, mutation]);

  return (
    <BoxFullWidth>
      <Typography variant={'h2'}>{TEXTS.LOGIN}</Typography>
      <Box mt={1.5} mb={2}>
        <Typography>{TEXTS.SIGN_IN_TO_YOUR_ACCOUNT}</Typography>
      </Box>
      <form onSubmit={submitFormHandler}>
        <ColumnBoxFlexFullWidth>
          <InputWithIcon name={'email'} type={'email'} placeholder={TEXTS.EMAIL} icon={'person'}
                         onChange={handleInputChange}
                         helperText={validationError['email']} required={true}
                         error={!!validationError['email']} autoComplete={'e-mail'}/>
          <InputWithIcon sx={{ mt: 2 }} name={'password'} autoComplete={'password'} type={'password'}
                         icon={'lock'} onChange={handleInputChange} required={true} placeholder={TEXTS.PASSWORD}
                         helperText={validationError['password']} error={!!validationError['password']}
          />
        </ColumnBoxFlexFullWidth>
        {
          responseError &&
          <Typography sx={{ color: theme.palette.error.main, my: 2 }}>
            {responseError}
          </Typography>
        }
        <FlexBoxFullWidth justifyContent={'space-between'} my={2}>
          <ButtonWithLoader type={'submit'} variant={'contained'} loading={mutation.isLoading}
          >{TEXTS.LOGIN}
          </ButtonWithLoader>
        </FlexBoxFullWidth>
      </form>
    </BoxFullWidth>
  );
};

export default Login;